'use client';

import { useState, useEffect } from 'react';
import styles from './dynamicInputList.module.css';

/**
 * DynamicInputList - A component for managing a list of input fields with add/remove functionality
 * @param {Object} props
 * @param {Array} props.values - Array of current values
 * @param {Function} props.onChange - Callback when values change (receives array of values)
 * @param {string} props.placeholder - Placeholder text for inputs
 * @param {string} props.label - Label for the field group
 * @param {ReactNode} props.icon - Icon component/element to display (default: two people icon)
 * @param {string} props.className - Additional CSS classes
 * @param {Function} props.validate - Optional validation function (receives value, returns error string or null)
 * @param {number} props.minItems - Minimum number of items (default: 1)
 * @param {number} props.maxItems - Maximum number of items (default: undefined, no limit)
 * @param {string} props.removeButtonText - Text for remove button (default: 'Pašalinti')
 * @param {string} props.addButtonText - Text for add button (default: 'Pridėti keliautoją')
 */
export default function DynamicInputList({
  values = [],
  onChange,
  placeholder = '',
  label = '',
  icon,
  className = '',
  validate,
  minItems = 1,
  maxItems,
  removeButtonText = 'Pašalinti',
  addButtonText = 'Pridėti keliautoją',
}) {
  const [localValues, setLocalValues] = useState(values || []);
  const [errors, setErrors] = useState({});

  // Default icon (two people)
  const defaultIcon = (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M1.75 11.6667C3.11254 10.2215 4.96243 9.33333 7 9.33333C9.03757 9.33333 10.8875 10.2215 12.25 11.6667M9.625 4.375C9.625 5.82475 8.44975 7 7 7C5.55025 7 4.375 5.82475 4.375 4.375C4.375 2.92525 5.55025 1.75 7 1.75C8.44975 1.75 9.625 2.92525 9.625 4.375Z"
        stroke="white"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  const displayIcon = icon || defaultIcon;

  useEffect(() => {
    setLocalValues(values || []);
  }, [values]);

  const handleInputChange = (index, value) => {
    const newValues = [...localValues];
    newValues[index] = value;
    setLocalValues(newValues);

    // Validate if validator provided
    if (validate) {
      const error = validate(value, index);
      setErrors(prev => ({
        ...prev,
        [index]: error || null,
      }));
    }

    // Notify parent
    if (onChange) {
      onChange(newValues);
    }
  };

  const handleAdd = () => {
    if (maxItems && localValues.length >= maxItems) {
      return;
    }
    const newValues = [...localValues, ''];
    setLocalValues(newValues);
    if (onChange) {
      onChange(newValues);
    }
  };

  const handleRemove = (index) => {
    if (localValues.length <= minItems) {
      return;
    }
    const newValues = localValues.filter((_, i) => i !== index);
    setLocalValues(newValues);
    
    // Remove error for this index
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[index];
      // Reindex errors
      const reindexed = {};
      Object.keys(newErrors).forEach(key => {
        const keyNum = parseInt(key);
        if (keyNum > index) {
          reindexed[keyNum - 1] = newErrors[key];
        } else {
          reindexed[key] = newErrors[key];
        }
      });
      return reindexed;
    });

    if (onChange) {
      onChange(newValues);
    }
  };

  // Ensure at least minItems exist
  useEffect(() => {
    if (localValues.length < minItems) {
      const newValues = [...localValues];
      while (newValues.length < minItems) {
        newValues.push('');
      }
      setLocalValues(newValues);
      if (onChange) {
        onChange(newValues);
      }
    }
  }, [minItems, localValues.length, onChange]);

  return (
    <div className={`${styles.container} ${className}`}>
      {label && <label className={styles.label}>{label}</label>}
      <div className={styles.inputList}>
        {localValues.map((value, index) => (
          <div key={index} className={styles.inputWrapper}>
            <div className={styles.inputContainer}>
              <div className={styles.iconBadge}>
                {displayIcon}
                <span className={styles.badgeNumber}>{index + 1}</span>
              </div>
              <input
                type="text"
                className={`${styles.input} ${errors[index] ? styles.inputError : ''}`}
                placeholder={placeholder}
                value={value}
                onChange={(e) => handleInputChange(index, e.target.value)}
              />
              {localValues.length > minItems && (
                <button
                  type="button"
                  className={styles.removeButton}
                  onClick={() => handleRemove(index)}
                  aria-label={removeButtonText}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <g clipPath="url(#clip0_18800_6989)">
                      <path
                        d="M10.0002 6.00065L6.00016 10.0007M6.00016 6.00065L10.0002 10.0007M14.6668 8.00065C14.6668 11.6825 11.6821 14.6673 8.00016 14.6673C4.31826 14.6673 1.3335 11.6825 1.3335 8.00065C1.3335 4.31875 4.31826 1.33398 8.00016 1.33398C11.6821 1.33398 14.6668 4.31875 14.6668 8.00065Z"
                        stroke="currentColor"
                        strokeWidth="1.66667"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_18800_6989">
                        <rect width="16" height="16" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                  <span className={styles.removeButtonText}>{removeButtonText}</span>
                </button>
              )}
            </div>
            {errors[index] && (
              <div className={styles.errorMessage}>{errors[index]}</div>
            )}
          </div>
        ))}
      </div>
      {(!maxItems || localValues.length < maxItems) && (
        <button
          type="button"
          className={styles.addButton}
          onClick={handleAdd}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M8.00016 3.33398V12.6673M3.3335 8.00065H12.6668"
              stroke="currentColor"
              strokeWidth="1.66667"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span>{addButtonText}</span>
        </button>
      )}
    </div>
  );
}

